<i18n
  src="@/locales/views/agro_exporter/finances/container_outcomes/edit_org_invoice.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/org_invoice.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_item.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_org_invoice')"
    :params="{
      agro_exporter_id: agro_exporter_id,
      agro_exporter_process_id: agro_exporter_process_id,
      org_invoice_id: org_invoice_id,
      container_id: org_invoice.container_id
    }"
  />

  <bread-crumbs :labels="[module_name, agro_exporter_process.name]" />

  <template v-if="load_general_data">
    <div class="mt-8">
      <container-info
        :agro_exporter_id="agro_exporter_id"
        :container="container"
      />
    </div>

    <alert-danger class="mt-6" :errors_data="std_err" />

    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y divide-slate-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <div class="mt-1">
                  <switch-form
                    v-model="use_invoice_issuer"
                    :label="$t('labels.use_issuer_invoice')"
                  />
                </div>
              </div>
              <div v-if="use_invoice_issuer" class="sm:col-span-6">
                <label
                  :class="['label-form', isInvalidLabel('invoice_issuer_id')]"
                >
                  {{ $t("model.org_invoice.invoice_issuer") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="invoice_issuers"
                    v-model="org_invoice.invoice_issuer_id"
                    :is_invalid="isInvalidSelect('invoice_issuer_id')"
                  ></select-form>
                </div>
              </div>
              <div
                v-if="!use_invoice_issuer && container_providers.length > 1"
                class="sm:col-span-6"
              >
                <label
                  :class="['label-form', isInvalidLabel('invoice_issuer_id')]"
                >
                  Proveedor
                </label>
                <div class="mt-1">
                  <select-form
                    :options="container_providers"
                    v-model="provider_invoice_issuer_id"
                    :is_invalid="isInvalidSelect('invoice_issuer_id')"
                  ></select-form>
                </div>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('invoice_code')]">
                  {{ $t("model.org_invoice.invoice_code") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model="org_invoice.invoice_code"
                    :class="['input-text', isInvalid('invoice_code')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('invoice_date')]">
                  {{ $t("model.org_invoice.invoice_date") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="org_invoice.invoice_date" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('receipt_date')]">
                  {{ $t("model.org_invoice.receipt_date") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="org_invoice.receipt_date" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label
                  :class="[
                    'label-form',
                    isInvalidLabel('scheduled_payment_at')
                  ]"
                >
                  {{ $t("model.org_invoice.scheduled_payment_at") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="org_invoice.scheduled_payment_at" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('amount')]">
                  {{ $t("model.org_invoice.amount") }}
                </label>
                <div class="mt-1 flex relative">
                  <select-form
                    class="w-1/3"
                    :options="currencies"
                    v-model="org_invoice.currency_id"
                    :disabled="currencies.length == 1"
                    :is_invalid="isInvalidSelect('currency')"
                  ></select-form>
                  <div class="flex flex-1 relative">
                    <div
                      class="flex items-center absolute inset-y-0 left-0 ml-3 text-sm leading-5"
                    >
                      {{ currency_symbol }}
                    </div>
                    <input
                      type="number"
                      v-model.number="org_invoice.amount"
                      :disabled="org_invoice_items_params.length > 0"
                      :class="['input-text pl-6', isInvalid('amount')]"
                    />
                  </div>
                </div>
              </div>

              <div class="sm:col-span-3">
                <label
                  :class="['label-form', isInvalidLabel('detraction_percent')]"
                >
                  {{ $t("model.org_invoice.detraction_percent") }}
                </label>
                <div class="mt-1">
                  <input
                    type="number"
                    v-model.number="org_invoice.detraction_percent"
                    :class="['input-text', isInvalid('detraction_percent')]"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('exchange_rate')]">
                  {{ $t("model.org_invoice.exchange_rate") }}
                </label>
                <div class="mt-1">
                  <input
                    type="number"
                    v-model.number="org_invoice.exchange_rate"
                    :class="['input-text', isInvalid('exchange_rate')]"
                  />
                </div>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-4">
              <div class="sm:col-span-3">
                <div class="mt-1">
                  <switch-form
                    v-model="org_invoice.fairtrade"
                    :label="$t('model.org_invoice.fairtrade')"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <div class="mt-1">
                  <switch-form
                    v-model="org_invoice.fairtrade_incentive"
                    :label="$t('model.org_invoice.fairtrade_incentive')"
                  />
                </div>
              </div>
            </div>
            <template
              v-for="(org_invoice_item, index) in org_invoice_items"
              :key="index"
            >
              <div class="py-4" v-if="!org_invoice_item._destroy">
                <div class="flex justify-end">
                  <span @click.prevent="removeOrgInvoiceItem(index)">
                    <i class="fa-solid fa-xmark fa-xl text-gray-400"></i>
                  </span>
                </div>
                <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                  <div class="sm:col-span-1">
                    <label
                      :class="[
                        'label-form',
                        isInvalidLabel(index + '_quantity')
                      ]"
                    >
                      {{ $t("model.org_invoice_item.quantity") }}
                    </label>
                    <div class="mt-1">
                      <input
                        type="text"
                        v-model.trim="org_invoice_item.quantity"
                        :class="['input-text', isInvalid(index + '_quantity')]"
                      />
                    </div>
                  </div>
                  <div class="sm:col-span-2">
                    <label
                      :class="[
                        'label-form',
                        isInvalidLabel('exporter_process_crop_package')
                      ]"
                    >
                      {{
                        $t(
                          "model.org_invoice_item.exporter_process_crop_package"
                        )
                      }}
                    </label>
                    <div class="mt-1">
                      <select-form
                        :options="exporter_process_crop_packages"
                        v-model="
                          org_invoice_item.exporter_process_crop_package_id
                        "
                        :is_invalid="
                          isInvalidSelect('exporter_process_crop_package')
                        "
                        label="label"
                      ></select-form>
                    </div>
                  </div>
                  <div class="sm:col-span-4">
                    <label
                      :class="[
                        'label-form',
                        isInvalidLabel(index + '_description')
                      ]"
                    >
                      {{ $t("model.org_invoice_item.description") }}
                    </label>
                    <div class="mt-1">
                      <input
                        type="text"
                        v-model.trim="org_invoice_item.description"
                        :class="[
                          'input-text',
                          isInvalid(index + '_description')
                        ]"
                      />
                    </div>
                  </div>
                  <div class="sm:col-span-1">
                    <label
                      :class="[
                        'label-form',
                        isInvalidLabel(index + '_unit_price')
                      ]"
                    >
                      {{ $t("model.org_invoice_item.unit_price") }}
                    </label>
                    <div class="mt-1">
                      <input
                        type="text"
                        v-model.trim="org_invoice_item.unit_price"
                        :class="[
                          'input-text',
                          isInvalid(index + '_unit_price')
                        ]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <div class="flex justify-end mt-4">
            <white-button
              @click.prevent="addOrgInvoiceItem()"
              :label="$t('labels.add_item_button')"
            />
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="goToShowOrgInvoice()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!proc_save_button"
              @click.prevent="updateOrgInvoice()"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import moment from "moment";
import Requester from "@/views/agro_exporter/requesters/finances/container_outcomes/manage_org_invoice.js";
import ContainerInfo from "@/components/views/agro_exporter/finances/container_outcomes/ContainerInfo.vue";

export default {
  name: "OrgFinancesContainerIncomesEditExportInvoice",
  components: { ContainerInfo },
  props: {
    agro_exporter_process_id: {
      required: true
    },
    agro_exporter_id: {
      required: true
    },
    org_invoice_id: {
      required: true
    }
  },
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      agro_exporter_process: {},
      container: {},
      currencies: [],
      exporter_process_crop_packages: [],
      org_invoice: {},
      org_invoice_item_default: {
        id: null,
        quantity: null,
        description: null,
        unit_price: null,
        _destroy: false
      },
      org_invoice_items: [],
      use_invoice_issuer: false,
      provider_invoice_issuer_id: null,
      container_providers: [],
      invoice_issuers: [],
      load_container_providers: false
    };
  },
  methods: {
    fetchData: async function () {
      await this.fetchAgroExporterProcess();
      await this.fetchOrgInvoice();
      await this.fetchContainer();
      await this.fetchExporterProcessCropPackages();
      await this.fetchCurrencies();
      this.load_general_data = true;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchContainer: async function () {
      let response = await Requester.fetchContainer(
        this.agro_exporter_id,
        this.org_invoice.container_id
      );
      this.container = response.data;
    },
    fetchCurrencies: async function () {
      let response = await Requester.fetchCurrencies(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.currencies = response.data;
    },
    fetchOrgInvoice: async function () {
      let response = await Requester.fetchOrgInvoice(
        this.agro_exporter_id,
        this.org_invoice_id
      );
      response.data.detraction_percent = response.data.detraction_percent * 100;
      this.org_invoice = response.data;
      if (this.org_invoice.issuer_type === 2) {
        this.use_invoice_issuer = true;
        this.fetchInvoiceIssuers();
      } else {
        this.use_invoice_issuer = false;
        this.fetchContainerProviders();
      }
      this.org_invoice_items = this.org_invoice.invoice_items.map(item => {
        return {
          id: item.id,
          quantity: item.quantity,
          description: item.description,
          unit_price: item.unit_price,
          exporter_process_crop_package_id:
            item.exporter_process_crop_package_id,
          _destroy: false
        };
      });
    },
    fetchExporterProcessCropPackages: async function () {
      let response = await Requester.fetchExporterProcessCropPackages(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.exporter_process_crop_packages = response.data;
    },
    fetchContainerProviders: async function () {
      let response = await Requester.fetchContainerProviders(
        this.agro_exporter_id,
        this.org_invoice.container_id
      );
      this.container_providers = response.data;
      this.load_container_providers = true;
      if (this.container_providers.length == 1)
        this.provider_invoice_issuer_id = this.container_providers[0].id;
    },
    fetchInvoiceIssuers: async function () {
      if (!this.use_invoice_issuer) return;
      let response = await Requester.fetchInvoiceIssuers(
        this.agro_exporter_id,
        this.org_invoice.container_id
      );
      this.invoice_issuers = response.data;
      if (this.invoice_issuers.length == 1)
        this.org_invoice.invoice_issuer_id = this.invoice_issuers[0].id;
    },
    addOrgInvoiceItem: function () {
      this.org_invoice_items.push({ ...this.org_invoice_item_default });
    },
    removeOrgInvoiceItem: function (index) {
      if (this.org_invoice_items[index].id) {
        this.org_invoice_items[index]._destroy = true;
      } else this.org_invoice_items.splice(index, 1);

      if (this.org_invoice_items.filter(item => !item._destroy).length == 0) {
        this.addOrgInvoiceItem();
      }
    },
    updateOrgInvoice: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.update(
          this.agro_exporter_id,
          this.org_invoice_id,
          {
            invoice_date: this.invoice_date_formatted,
            invoice_code: this.org_invoice.invoice_code,
            amount: this.org_invoice.amount,
            detraction_percent: this.org_invoice.detraction_percent,
            exchange_rate: this.org_invoice.exchange_rate,
            container_id: this.org_invoice.container_id,
            currency_id: this.org_invoice.currency_id,
            receipt_date: this.org_invoice.receipt_date,
            scheduled_payment_at: this.org_invoice.scheduled_payment_at,
            org_invoice_items: this.org_invoice_items,
            invoice_issuer_id: this.org_invoice.invoice_issuer_id,
            issuer_type: this.org_invoice.issuer_type,
            fairtrade: this.org_invoice.fairtrade,
            fairtrade_incentive: this.org_invoice.fairtrade_incentive
          }
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowOrgInvoice();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_save_button = false;
    },
    goToShowOrgInvoice: function () {
      this.$router.push({
        name: this.routeName("show_org_invoice"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          org_invoice_id: this.org_invoice_id,
          container_id: this.org_invoice.container_id
        }
      });
    }
  },
  computed: {
    invoice_date_formatted: function () {
      return moment(this.org_invoice.invoice_date).format("YYYY-MM-DD");
    },
    currency_symbol: function () {
      return this.currencies.find(
        currency => currency.id == this.org_invoice.currency_id
      )?.symbol;
    },
    org_invoice_items_params: function () {
      return this.org_invoice_items.filter(
        org_invoice_item =>
          org_invoice_item.id ||
          org_invoice_item.quantity ||
          org_invoice_item.description ||
          org_invoice_item.unit_price
      );
    },
    provider_invoice_issuer: function () {
      return this.container_providers.find(
        provider => provider.id == this.provider_invoice_issuer_id
      );
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => {
        this.fetchData();
      })
    );
  },
  watch: {
    org_invoice_items: {
      deep: true,
      handler: function (new_items) {
        let total_amount = 0;
        new_items.forEach(item => {
          if (item.quantity && item.unit_price) total_amount += Number(item.quantity) * Number(item.unit_price);
        })
        console.log(total_amount);
        if (total_amount) this.org_invoice.amount = total_amount 
        else this.org_invoice.amount = null;
      }
    },
    provider_invoice_issuer: function () {
      if (this.provider_invoice_issuer?.producer_id) {
        this.org_invoice.issuer_type = 0;
        this.org_invoice.invoice_issuer_id =
          this.provider_invoice_issuer.producer_id;
      } else if (this.provider_invoice_issuer?.organization_id) {
        this.org_invoice.issuer_type = 1;
        this.org_invoice.invoice_issuer_id =
          this.provider_invoice_issuer.organization_id;
      } else {
        this.org_invoice.invoice_issuer_id = null;
      }
    },
    use_invoice_issuer: async function (new_value) {
      this.provider_invoice_issuer_id = null;
      if (new_value) {
        this.org_invoice.invoice_issuer_id = null;
        this.org_invoice.issuer_type = 2;
        this.fetchInvoiceIssuers();
      } else {
        this.org_invoice.issuer_type = null;
        if (!this.load_container_providers)
          await this.fetchContainerProviders();
        if (this.container_providers.length == 1)
          this.provider_invoice_issuer_id = this.container_providers[0].id;
      }
    }
  }
};
</script>
